import React, { useState, useMemo } from 'react';
import { Expand, Car } from 'lucide-react';
import ImageLightbox from './ImageLightbox';

/**
 * CarGallery — premium main-image + thumbnail gallery (read-only).
 * Reused on the car details view and the public reservation page.
 *
 * Props:
 *   images:      array of { url } | string[]  (ordered; first is the cover)
 *   fallback:    optional single URL (e.g. car.photo) used when images is empty
 *   alt:         string
 *   isAr:        boolean
 */
export default function CarGallery({ images = [], fallback = '', alt = '', isAr = false }) {
  const urls = useMemo(() => {
    const list = (images || []).map((x) => (typeof x === 'string' ? x : x?.url)).filter(Boolean);
    if (!list.length && fallback) return [fallback];
    return list;
  }, [images, fallback]);

  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const idx = Math.min(active, Math.max(0, urls.length - 1));

  if (!urls.length) {
    return (
      <div className="flex aspect-[16/10] w-full items-center justify-center rounded-2xl border border-gray-200 bg-gray-50 text-gray-300">
        <div className="flex flex-col items-center gap-2">
          <Car className="h-10 w-10" />
          <span className="text-xs font-medium text-gray-400">{isAr ? 'لا توجد صور' : 'Aucune photo'}</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Main image */}
      <button
        type="button"
        onClick={() => setLightbox(true)}
        className="group relative block aspect-[16/10] w-full overflow-hidden rounded-2xl border border-gray-200 bg-gray-100"
      >
        <img
          src={urls[idx]}
          alt={alt}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
        />
        <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-black/50 px-2.5 py-1 text-[11px] font-semibold text-white opacity-0 backdrop-blur transition-opacity group-hover:opacity-100">
          <Expand className="h-3.5 w-3.5" /> {isAr ? 'تكبير' : 'Agrandir'}
        </span>
        {urls.length > 1 && (
          <span className="absolute bottom-3 right-3 rounded-full bg-black/50 px-2 py-0.5 text-[11px] font-semibold text-white backdrop-blur">
            {idx + 1} / {urls.length}
          </span>
        )}
      </button>

      {/* Thumbnails */}
      {urls.length > 1 && (
        <div className="mt-2.5 flex gap-2 overflow-x-auto pb-1">
          {urls.map((src, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setActive(i)}
              className={`h-14 w-20 shrink-0 overflow-hidden rounded-lg border-2 transition ${
                i === idx ? 'border-blue-500 ring-2 ring-blue-100' : 'border-transparent opacity-70 hover:opacity-100'
              }`}
            >
              <img src={src} alt="" loading="lazy" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}

      {lightbox && (
        <ImageLightbox
          images={urls}
          index={idx}
          onIndexChange={setActive}
          onClose={() => setLightbox(false)}
        />
      )}
    </div>
  );
}
